import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine, ArrowUpFromLine, ArrowLeft, Camera, Check, CheckCircle2,
  ChevronRight, Loader2, LogOut, Pencil, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { validateBookingForOut, CARRIERS_REQUIRING_BOOKING_ON_OUT } from "../lib/ediRequirements";
import { autoSendWaybill, carrierFromContainerNumber } from "../components/GateEventFab";

const ROLES_CAN_GATE = new Set(["admin", "supervisor", "depot_manager", "operator"]);

const ISO_TILES = [
  { iso: "22T6", label: "20' TANK", sub: "ISO Tank" },
  { iso: "22G1", label: "20' DRY", sub: "General" },
  { iso: "42G1", label: "40' DRY", sub: "General" },
  { iso: "45G1", label: "40' HC", sub: "High Cube" },
  { iso: "22R1", label: "20' REEF", sub: "Reefer" },
  { iso: "45R1", label: "40' REEF", sub: "Reefer HC" },
];

const CN_RE = /^[A-Z]{3,4}\d{6,7}$/;

const LOC_DEFAULTS = {
  "GH-TEM": { yard: "TEMA", loc: "GHTEM", loc_name: "Multi Purpose Terminal Ghana (MTG)" },
  "TG-LFW": { yard: "LOME", loc: "TGLFW", loc_name: "Lom\u00e9 Depot" },
  "GA-LBV": { yard: "LBV",  loc: "GALBV", loc_name: "Libreville Depot" },
};

function blankForm(initialDepot = "GH-TEM") {
  const d = LOC_DEFAULTS[(initialDepot || "GH-TEM").toUpperCase()] || LOC_DEFAULTS["GH-TEM"];
  return {
    container_number: "",
    container_iso_type: "22T6",
    gate_type: "IN",
    is_full: false,
    location_code: d.loc,
    location_name: d.loc_name,
    yard_code: d.yard,
    carrier_code: "",
    last_carrier: "",
    tank_category: "",
    customer: "",
    booking_number: "",
    seal_number: "",
    truck_plate: "",
    driver_name: "",
    grade: "B",
    condition: "SOUND",
    notes: "",
    _carrier_manually_set: false,
  };
}

/* ── Shared bits ──────────────────────────────────────────────────────────── */

function StepHeader({ title, sub, onBack, step, total }) {
  return (
    <div className="sticky top-0 z-20 bg-[#003147] text-white px-4 pt-3 pb-3 flex items-center gap-3">
      {onBack && (
        <button type="button" onClick={onBack} className="h-10 w-10 -ml-1 flex items-center justify-center rounded-full active:bg-white/10" data-testid="gate-app-back">
          <ArrowLeft className="w-6 h-6" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">{sub}</div>
        <div className="font-display text-lg font-extrabold leading-tight truncate">{title}</div>
      </div>
      {step && (
        <div className="text-[11px] font-mono text-white/70 shrink-0">{step}/{total}</div>
      )}
    </div>
  );
}

function BigButton({ children, onClick, disabled, variant = "primary", testid }) {
  const styles = {
    primary: "bg-[#006E9E] text-white active:bg-[#003147]",
    dark: "bg-gray-900 text-white active:bg-black",
    ghost: "bg-white text-gray-700 border-2 border-gray-200 active:bg-gray-50",
  };
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`w-full h-14 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${styles[variant]}`}
    >
      {children}
    </button>
  );
}

function Label({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500 mb-1.5">{children}</div>;
}

/* ── OCR scan block ───────────────────────────────────────────────────────── */

function ScanBlock({ value, onChange, onScanned }) {
  const camRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [confidence, setConfidence] = useState(null);

  const handleCapture = async (f) => {
    if (!f) return;
    setScanning(true);
    setConfidence(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ocr/container-number", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.container_number);
      setConfidence(data.confidence);
      onScanned?.(data.container_number);
      if (navigator.vibrate) navigator.vibrate(60);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not read the number — try again or type it in");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      <input
        ref={camRef} type="file" accept="image/jpeg,image/png,image/webp"
        capture="environment" className="hidden"
        onChange={(e) => { handleCapture(e.target.files?.[0]); e.target.value = ""; }}
        data-testid="gate-app-ocr-input"
      />
      <button
        type="button" disabled={scanning}
        onClick={() => camRef.current?.click()}
        data-testid="gate-app-scan-btn"
        className="w-full rounded-2xl border-4 border-dashed border-[#006E9E]/40 bg-[#006E9E]/5 active:bg-[#006E9E]/10 py-8 flex flex-col items-center gap-2 transition-colors disabled:opacity-60"
      >
        {scanning
          ? <Loader2 className="w-12 h-12 text-[#006E9E] animate-spin" />
          : <Camera className="w-12 h-12 text-[#006E9E]" />}
        <span className="font-bold text-[#003147] text-lg">{scanning ? "Reading number…" : "Scan with camera"}</span>
        <span className="text-xs text-gray-500">Point at the 4-letter + 7-digit marking</span>
      </button>

      <div className="mt-4">
        <Label>Or type it in</Label>
        <input
          type="text" inputMode="text" autoCapitalize="characters" autoComplete="off"
          value={value} placeholder="MSCU1234567"
          onChange={(e) => { onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")); setConfidence(null); }}
          className="w-full h-14 rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-4 font-mono font-black text-2xl tracking-wider text-center"
          data-testid="gate-app-container-input"
        />
        {confidence && (
          <div className="mt-1.5 text-xs text-emerald-700 font-semibold flex items-center gap-1" data-testid="gate-app-ocr-confidence">
            <CheckCircle2 className="w-3.5 h-3.5" /> Scanned — confidence {confidence.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Photos block (Phase 2) ───────────────────────────────────────────────── */

function PhotoBlock({ photos, setPhotos }) {
  const camRef = useRef(null);
  const addFiles = (list) => {
    const room = 6 - photos.length;
    const fresh = Array.from(list || []).slice(0, room).map((file) => ({
      file, caption: "", isDamage: false,
      preview: URL.createObjectURL(file),
      key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
    }));
    setPhotos((p) => [...p, ...fresh]);
  };
  return (
    <div>
      <input
        ref={camRef} type="file" accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
        capture="environment" multiple className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        data-testid="gate-app-photo-input"
      />
      {photos.length < 6 && (
        <button
          type="button" onClick={() => camRef.current?.click()}
          data-testid="gate-app-add-photo"
          className="w-full rounded-2xl border-4 border-dashed border-gray-300 active:border-[#006E9E] py-7 flex flex-col items-center gap-1.5"
        >
          <Camera className="w-10 h-10 text-gray-400" />
          <span className="font-bold text-gray-700">Take photo</span>
          <span className="text-xs text-gray-500">{photos.length}/6 · mark damage shots red</span>
        </button>
      )}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          {photos.map((p, idx) => (
            <div key={p.key} className={`relative rounded-xl border-2 p-1.5 bg-white ${p.isDamage ? "border-red-500" : "border-gray-200"}`} data-testid={`gate-app-photo-${idx}`}>
              <img src={p.preview} alt="" className="w-full h-28 object-cover rounded-lg bg-gray-100" />
              <button
                type="button"
                onClick={() => setPhotos((prev) => prev.filter((x) => x.key !== p.key))}
                className="absolute top-2.5 right-2.5 bg-white/95 border border-gray-300 w-8 h-8 rounded-full flex items-center justify-center"
                data-testid={`gate-app-photo-remove-${idx}`}
              >
                <X className="w-4 h-4 text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() => setPhotos((prev) => prev.map((x) => x.key === p.key ? { ...x, isDamage: !x.isDamage } : x))}
                data-testid={`gate-app-photo-damage-${idx}`}
                className={`mt-1.5 w-full h-9 rounded-lg text-xs font-bold uppercase tracking-wider ${p.isDamage ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"}`}
              >
                {p.isDamage ? "⚠ Damage shot" : "Mark as damage"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main app ─────────────────────────────────────────────────────────────── */

export default function GateApp() {
  const { user, logout, depot } = useAuth();
  const [step, setStep] = useState("home"); // home | container | details | photos | review | done
  const [form, setForm] = useState(() => blankForm(depot));
  const [photos, setPhotos] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [yards, setYards] = useState([]);
  const [partners, setPartners] = useState([]);
  const [intakeMatch, setIntakeMatch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(null);
  const [todayCount, setTodayCount] = useState(null);

  useEffect(() => {
    api.get("/containers/carriers").then((r) => setCarriers(r.data || [])).catch(() => {});
    api.get("/partners?fields=code,legal_name").then((r) => setPartners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    const from = new Date(); from.setHours(0, 0, 0, 0);
    api.get(`/gate-events?date_from=${from.toISOString()}&limit=500`)
      .then((r) => setTodayCount((r.data || []).filter((e) => e.status !== "CANCELLED").length))
      .catch(() => {});
  }, []);

  // Re-fetch yards whenever the picked depot changes so the picker stays
  // in sync (and the form falls onto a valid yard for the new depot).
  useEffect(() => {
    api.get("/yards").then((r) => {
      const list = r.data || [];
      setYards(list);
      const codes = new Set(list.map((y) => y.code));
      setForm((f) => {
        if (codes.has(f.yard_code)) return f;
        const def = (list.find((y) => y.default) || list[0] || {}).code || "";
        return { ...f, yard_code: def };
      });
    }).catch(() => {});
  }, [depot]);

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "container_number" && !f._carrier_manually_set) {
      const s = carrierFromContainerNumber(v);
      next.carrier_code = s || "";
      next.last_carrier = s || "";
    }
    return next;
  });

  const pickCarrier = (code) => setForm((f) => {
    const isStolt = ["STC", "STOLT_F", "STOLT_C"].includes(code);
    let cat = f.tank_category, cc = code;
    if (code === "STOLT_F") { cat = "FOOD"; cc = "STC"; }
    else if (code === "STOLT_C") { cat = "CHEM"; cc = "STC"; }
    return { ...f, carrier_code: cc, last_carrier: cc, tank_category: isStolt ? cat : f.tank_category, _carrier_manually_set: true };
  });

  // Expected-arrival lookup (STC depot instructions)
  useEffect(() => {
    const cn = (form.container_number || "").trim();
    if (step !== "container" || !CN_RE.test(cn)) { setIntakeMatch(null); return; }
    const t = setTimeout(() => {
      api.get(`/document-intake/by-tank/${cn}`)
        .then((r) => setIntakeMatch(r.data?.found ? r.data : null))
        .catch(() => setIntakeMatch(null));
    }, 300);
    return () => clearTimeout(t);
  }, [step, form.container_number]);

  const applyIntake = () => {
    if (!intakeMatch) return;
    setForm((f) => {
      const prev = intakeMatch.previous_cargo || "";
      const next = intakeMatch.product || "";
      let cargoLine = "";
      if (prev && next) cargoLine = `Cargoes: ${prev} → ${next}`;
      else if (prev) cargoLine = `Previous cargo: ${prev}`;
      else if (next) cargoLine = `Next load: ${next}`;
      const poLine = intakeMatch.po_number ? `STC PO ${intakeMatch.po_number}` : "";
      const eftco = (intakeMatch.eftco_codes || []).length ? `EFTCO: ${intakeMatch.eftco_codes.join(" ")}` : "";
      return {
        ...f,
        carrier_code: "STC", last_carrier: "STC", _carrier_manually_set: true,
        customer: f.customer || "STOLT TANK CONTAINERS",
        container_iso_type: "22T6",
        tank_category: intakeMatch.tank_category || f.tank_category || "CHEM",
        seal_number: (intakeMatch.seal_numbers || [])[0] || f.seal_number,
        notes: [f.notes, poLine, cargoLine, eftco].filter(Boolean).join(" · "),
      };
    });
    toast.success("Pre-filled from STC depot instruction");
    if (intakeMatch.id) api.patch(`/document-intake/${intakeMatch.id}`, { status: "ARRIVED" }).catch(() => {});
    setIntakeMatch(null);
  };

  const start = (gateType) => {
    setForm({ ...blankForm(depot), gate_type: gateType });
    setPhotos([]);
    setErr("");
    setSaved(null);
    setIntakeMatch(null);
    setStep("container");
  };

  const submit = async () => {
    if (submitting) return;
    setErr("");
    setSubmitting(true);
    try {
      const { _carrier_manually_set: _m, ...clean } = form;
      const payload = {
        ...clean,
        container_number: form.container_number.toUpperCase().trim(),
        event_time: new Date().toISOString(),
        tank_category: form.tank_category || null,
        repair_status: "NONE",
        idempotency_key: crypto.randomUUID(),
      };
      const { data } = await api.post("/gate-events", payload);

      let photoFail = 0;
      for (const p of photos) {
        try {
          const fd = new FormData();
          fd.append("file", p.file);
          fd.append("caption", p.caption || "");
          fd.append("is_damage", p.isDamage ? "true" : "false");
          await api.post(`/gate-events/${data.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (pe) { photoFail++; console.warn("photo upload failed", pe); }
      }
      if (photoFail) toast.error(`${photoFail} photo upload(s) failed`);

      autoSendWaybill(data, photos).catch(() => {});
      setSaved(data);
      setTodayCount((c) => (c == null ? c : c + 1));
      if (navigator.vibrate) navigator.vibrate([60, 40, 60]);
      setStep("done");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;
  if (!ROLES_CAN_GATE.has(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-red-600 px-6 text-center" data-testid="gate-app-forbidden">
        Your role ({user.role}) cannot record gate events. Ask an admin for operator access.
      </div>
    );
  }

  const isTank = form.container_iso_type?.includes("T");
  const cnValid = CN_RE.test(form.container_number.trim());
  const carrierUpper = (form.last_carrier || form.carrier_code || "").toUpperCase();
  const bookingRequired = form.gate_type === "OUT" && CARRIERS_REQUIRING_BOOKING_ON_OUT.has(carrierUpper);
  const detailsValid =
    (form.carrier_code || "").trim() &&
    (form.gate_type !== "OUT" || (form.customer || "").trim()) &&
    !(form.gate_type === "OUT" && validateBookingForOut(carrierUpper, form.booking_number));

  /* ── HOME ── */
  if (step === "home") {
    return (
      <div className="min-h-screen bg-[#003147] text-white flex flex-col" data-testid="gate-app-home">
        <div className="px-5 pt-6 pb-2 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/50">Portside Depot</div>
            <h1 className="font-display text-3xl font-black tracking-tight mt-1">Gate App</h1>
            <div className="text-sm text-white/60 mt-1">{user.name || user.email}</div>
          </div>
          <button type="button" onClick={logout} className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20" title="Sign out" data-testid="gate-app-logout">
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 mt-2">
          <div className="rounded-xl bg-white/10 px-4 py-3 flex items-center justify-between" data-testid="gate-app-today-count">
            <span className="text-sm text-white/70">Gate moves today</span>
            <span className="font-mono font-black text-2xl">{todayCount == null ? "—" : todayCount}</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center gap-4 px-5 pb-6">
          <button
            type="button" onClick={() => start("IN")} data-testid="gate-app-start-in"
            className="rounded-2xl bg-[#99D420] text-[#1f3500] py-8 flex flex-col items-center gap-2 active:scale-[0.98] transition-transform"
          >
            <ArrowDownToLine className="w-12 h-12" />
            <span className="font-display font-black text-3xl tracking-tight">GATE IN</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] opacity-70">Container arriving</span>
          </button>
          <button
            type="button" onClick={() => start("OUT")} data-testid="gate-app-start-out"
            className="rounded-2xl bg-[#F1471D] text-white py-8 flex flex-col items-center gap-2 active:scale-[0.98] transition-transform"
          >
            <ArrowUpFromLine className="w-12 h-12" />
            <span className="font-display font-black text-3xl tracking-tight">GATE OUT</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] opacity-70">Container leaving</span>
          </button>
        </div>

        <div className="px-5 pb-6 text-center">
          <Link to="/" className="text-xs text-white/50 underline underline-offset-4" data-testid="gate-app-full-app-link">
            Open full desktop app
          </Link>
        </div>
      </div>
    );
  }

  /* ── DONE ── */
  if (step === "done" && saved) {
    const edi = saved._auto_edi;
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="gate-app-done">
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
          <div className="w-24 h-24 rounded-full bg-[#99D420]/20 flex items-center justify-center">
            <Check className="w-14 h-14 text-[#5a8f00]" strokeWidth={3} />
          </div>
          <div className="font-display font-black text-2xl">Gate {saved.gate_type} recorded</div>
          <div className="font-mono font-black text-3xl tracking-wider text-[#003147]" data-testid="gate-app-done-container">{saved.container_number}</div>
          <div className="space-y-1 text-sm text-gray-600">
            {edi?.status === "SENT" && <div className="text-emerald-700 font-semibold" data-testid="gate-app-edi-sent">✓ EDI sent to carrier automatically</div>}
            {edi?.status === "FAILED" && <div className="text-red-600 font-semibold">EDI failed — office will re-fire it</div>}
            {saved._auto_offhire && (
              <div className="text-emerald-700 font-semibold">✓ Rental closed for {saved._auto_offhire.customer_code}</div>
            )}
            {photos.length > 0 && <div>{photos.length} photo{photos.length === 1 ? "" : "s"} attached</div>}
          </div>
        </div>
        <div className="px-5 pb-8 space-y-3">
          <BigButton variant="primary" onClick={() => start(saved.gate_type)} testid="gate-app-next-truck">
            <ChevronRight className="w-5 h-5" /> Next truck (Gate {saved.gate_type})
          </BigButton>
          <BigButton variant="ghost" onClick={() => setStep("home")} testid="gate-app-done-home">Home</BigButton>
        </div>
      </div>
    );
  }

  /* ── Wizard steps ── */
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid={`gate-app-step-${step}`}>
      {step === "container" && (
        <>
          <StepHeader title="Container number" sub={`Gate ${form.gate_type}`} step={1} total={4} onBack={() => setStep("home")} />
          <div className="flex-1 px-4 py-5 space-y-5 pb-32">
            <ScanBlock value={form.container_number} onChange={(v) => update("container_number", v)} />

            {intakeMatch && (
              <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="gate-app-intake-banner">
                <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm mb-1">
                  <Sparkles className="w-4 h-4" /> Expected from STC · PO {intakeMatch.po_number || "—"}
                </div>
                <div className="text-xs text-emerald-900 font-mono mb-3">
                  {intakeMatch.previous_cargo && <>prev {intakeMatch.previous_cargo} · </>}
                  {intakeMatch.product && <>next {intakeMatch.product}</>}
                </div>
                <button type="button" onClick={applyIntake} className="w-full h-11 rounded-lg bg-emerald-600 text-white font-bold text-sm" data-testid="gate-app-apply-intake">
                  Apply pre-fill
                </button>
              </div>
            )}

            <div>
              <Label>Container type</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="gate-app-iso-picker">
                {ISO_TILES.map((t) => (
                  <button
                    key={t.iso} type="button" onClick={() => update("container_iso_type", t.iso)}
                    data-testid={`gate-app-iso-${t.iso}`}
                    className={`rounded-xl border-2 py-3 px-1 text-center transition-colors ${
                      form.container_iso_type === t.iso ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-700 border-gray-200"
                    }`}
                  >
                    <div className="text-sm font-black leading-tight">{t.label}</div>
                    <div className={`text-[10px] font-mono mt-0.5 ${form.container_iso_type === t.iso ? "opacity-80" : "text-gray-400"}`}>{t.iso}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Load status</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => update("is_full", false)} data-testid="gate-app-load-empty"
                  className={`h-12 rounded-xl border-2 font-bold ${!form.is_full ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200"}`}>EMPTY</button>
                <button type="button" onClick={() => update("is_full", true)} data-testid="gate-app-load-full"
                  className={`h-12 rounded-xl border-2 font-bold ${form.is_full ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200"}`}>FULL</button>
              </div>
            </div>

            {isTank && (
              <div>
                <Label>Tank grade</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => update("tank_category", "FOOD")} data-testid="gate-app-cat-FOOD"
                    className={`h-12 rounded-xl border-2 font-black text-sm ${form.tank_category === "FOOD" ? "bg-[#99D420] text-[#1f3500] border-[#7eb010]" : "bg-white text-[#3a6500] border-[#99D420]/50"}`}>FOOD GRADE</button>
                  <button type="button" onClick={() => update("tank_category", "CHEM")} data-testid="gate-app-cat-CHEM"
                    className={`h-12 rounded-xl border-2 font-black text-sm ${form.tank_category === "CHEM" ? "bg-[#F1471D] text-white border-[#c0360e]" : "bg-white text-[#9b2a0f] border-[#F1471D]/50"}`}>CHEMICAL</button>
                </div>
              </div>
            )}
          </div>
          <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 px-4 py-4">
            <BigButton disabled={!cnValid} onClick={() => setStep("details")} testid="gate-app-next-details">
              Next: details <ChevronRight className="w-5 h-5" />
            </BigButton>
            {!cnValid && form.container_number.length > 3 && (
              <div className="text-center text-xs text-red-600 mt-2" data-testid="gate-app-cn-invalid">4 letters + 7 digits, e.g. MSCU1234567</div>
            )}
          </div>
        </>
      )}

      {step === "details" && (
        <>
          <StepHeader title={form.container_number} sub={`Gate ${form.gate_type} · details`} step={2} total={4} onBack={() => setStep("container")} />
          <div className="flex-1 px-4 py-5 space-y-5 pb-32">
            <datalist id="gate-app-customers">
              {partners.map((p) => <option key={p.code} value={p.legal_name || p.code} label={p.code} />)}
            </datalist>

            <div>
              <Label>Carrier *</Label>
              <div className="flex flex-wrap gap-2" data-testid="gate-app-carrier-chips">
                {carriers.slice(0, 12).map((c) => (
                  <button
                    key={c.carrier} type="button" onClick={() => pickCarrier(c.carrier)}
                    data-testid={`gate-app-carrier-${c.carrier}`}
                    className={`px-4 h-11 rounded-full border-2 font-mono font-bold text-sm ${
                      form.carrier_code === c.carrier ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-700 border-gray-300"
                    }`}
                  >
                    {c.carrier}
                  </button>
                ))}
              </div>
              <input
                type="text" value={form.carrier_code} autoCapitalize="characters" autoComplete="off"
                onChange={(e) => setForm((f) => ({ ...f, carrier_code: e.target.value.toUpperCase(), last_carrier: e.target.value.toUpperCase(), _carrier_manually_set: true }))}
                list="gate-app-carrier-codes" placeholder="Or type carrier code…"
                className="w-full h-12 rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-4 font-mono mt-2"
                data-testid="gate-app-carrier-input"
              />
              <datalist id="gate-app-carrier-codes">
                {partners.map((p) => <option key={p.code} value={p.code} label={p.legal_name} />)}
              </datalist>
            </div>

            {yards.length > 1 && (
              <div>
                <Label>Yard *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {yards.map((y, i) => (
                    <button key={y.code} type="button" onClick={() => update("yard_code", y.code)}
                      data-testid={`gate-app-yard-${y.code}`}
                      className={`h-14 rounded-xl border-2 font-bold text-sm ${
                        form.yard_code === y.code
                          ? ((i === 0 || y.default) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-[#F1471D] text-white border-[#F1471D]")
                          : "bg-white text-gray-700 border-gray-200"
                      }`}>
                      {y.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>{form.gate_type === "OUT" ? "Consignee / customer *" : "Customer (optional)"}</Label>
              <input type="text" value={form.customer} onChange={(e) => update("customer", e.target.value)}
                list="gate-app-customers" autoComplete="off" placeholder="Start typing…"
                className="w-full h-12 rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-4"
                data-testid="gate-app-customer-input" />
            </div>

            {form.gate_type === "OUT" && (
              <div>
                <Label>{bookingRequired ? `Booking / service order * (${carrierUpper} requires it)` : "Booking number (optional)"}</Label>
                <input type="text" value={form.booking_number} autoCapitalize="characters" autoComplete="off"
                  onChange={(e) => update("booking_number", e.target.value.toUpperCase())}
                  placeholder={bookingRequired ? `Required by ${carrierUpper}` : "e.g. HLCUBKG12345"}
                  className={`w-full h-12 rounded-xl border-2 focus:outline-none px-4 font-mono ${bookingRequired && !form.booking_number ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#006E9E]"}`}
                  data-testid="gate-app-booking-input" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Truck plate</Label>
                <input type="text" value={form.truck_plate} autoCapitalize="characters" autoComplete="off"
                  onChange={(e) => update("truck_plate", e.target.value.toUpperCase())}
                  className="w-full h-12 rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-3 font-mono"
                  data-testid="gate-app-truck-input" />
              </div>
              <div>
                <Label>Driver name</Label>
                <input type="text" value={form.driver_name} onChange={(e) => update("driver_name", e.target.value)}
                  className="w-full h-12 rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-3"
                  data-testid="gate-app-driver-input" />
              </div>
            </div>

            <div>
              <Label>Condition</Label>
              <div className="grid grid-cols-3 gap-2">
                {[["SOUND", "Sound"], ["DAMAGED", "Damaged"], ["UNDER_SURVEY", "Survey"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => update("condition", v)} data-testid={`gate-app-cond-${v}`}
                    className={`h-12 rounded-xl border-2 font-bold text-sm ${
                      form.condition === v
                        ? (v === "SOUND" ? "bg-[#99D420] text-[#1f3500] border-[#7eb010]" : "bg-[#F1471D] text-white border-[#c0360e]")
                        : "bg-white text-gray-700 border-gray-200"
                    }`}>{l}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Grade</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {["A", "B", "C"].map((g) => (
                    <button key={g} type="button" onClick={() => update("grade", g)} data-testid={`gate-app-grade-${g}`}
                      className={`h-12 rounded-xl border-2 font-black ${form.grade === g ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-700 border-gray-200"}`}>{g}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Seal number</Label>
                <input type="text" value={form.seal_number} autoCapitalize="characters" autoComplete="off"
                  onChange={(e) => update("seal_number", e.target.value.toUpperCase())}
                  className="w-full h-12 rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-3 font-mono"
                  data-testid="gate-app-seal-input" />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2}
                className="w-full rounded-xl border-2 border-gray-200 focus:border-[#006E9E] focus:outline-none px-3 py-2 text-sm"
                data-testid="gate-app-notes-input" />
            </div>
          </div>
          <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 px-4 py-4">
            <BigButton disabled={!detailsValid} onClick={() => setStep("photos")} testid="gate-app-next-photos">
              Next: photos <ChevronRight className="w-5 h-5" />
            </BigButton>
            {!form.carrier_code && (
              <div className="text-center text-xs text-red-600 mt-2" data-testid="gate-app-carrier-required">Pick a carrier — otherwise the EDI won't fire</div>
            )}
          </div>
        </>
      )}

      {step === "photos" && (
        <>
          <StepHeader title="Photos" sub={`${form.container_number} · Gate ${form.gate_type}`} step={3} total={4} onBack={() => setStep("details")} />
          <div className="flex-1 px-4 py-5 pb-32">
            <PhotoBlock photos={photos} setPhotos={setPhotos} />
            {form.condition === "DAMAGED" && photos.filter((p) => p.isDamage).length === 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border-2 border-amber-300 px-4 py-3 text-sm text-amber-900" data-testid="gate-app-damage-nudge">
                Condition is <b>DAMAGED</b> — take at least one photo and mark it as a damage shot for the survey record.
              </div>
            )}
          </div>
          <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 px-4 py-4 space-y-2">
            <BigButton onClick={() => setStep("review")} testid="gate-app-next-review">
              {photos.length ? `Continue with ${photos.length} photo${photos.length === 1 ? "" : "s"}` : "Skip photos"} <ChevronRight className="w-5 h-5" />
            </BigButton>
          </div>
        </>
      )}

      {step === "review" && (
        <>
          <StepHeader title="Confirm" sub={`Gate ${form.gate_type}`} step={4} total={4} onBack={() => setStep("photos")} />
          <div className="flex-1 px-4 py-5 pb-36 space-y-4">
            {err && (
              <div className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="gate-app-submit-error">{err}</div>
            )}
            <div className={`rounded-2xl p-5 text-white ${form.gate_type === "IN" ? "bg-[#003147]" : "bg-[#7a2410]"}`} data-testid="gate-app-review-card">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60">Gate {form.gate_type} · {form.is_full ? "FULL" : "EMPTY"}</div>
              <div className="font-mono font-black text-3xl tracking-wider mt-1">{form.container_number}</div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><span className="text-white/50 text-xs block">Type</span>{form.container_iso_type}{form.tank_category ? ` · ${form.tank_category}` : ""}</div>
                <div><span className="text-white/50 text-xs block">Carrier</span>{form.carrier_code || "—"}</div>
                <div><span className="text-white/50 text-xs block">Yard</span>{form.yard_code}</div>
                <div><span className="text-white/50 text-xs block">Condition</span>{form.condition} · Grade {form.grade}</div>
                {form.customer && <div className="col-span-2"><span className="text-white/50 text-xs block">Customer</span>{form.customer}</div>}
                {form.booking_number && <div className="col-span-2"><span className="text-white/50 text-xs block">Booking</span><span className="font-mono">{form.booking_number}</span></div>}
                {(form.truck_plate || form.driver_name) && (
                  <div className="col-span-2"><span className="text-white/50 text-xs block">Truck</span>{[form.truck_plate, form.driver_name].filter(Boolean).join(" · ")}</div>
                )}
                {photos.length > 0 && <div className="col-span-2"><span className="text-white/50 text-xs block">Photos</span>{photos.length} attached{photos.some((p) => p.isDamage) ? " (incl. damage)" : ""}</div>}
              </div>
            </div>
            <button type="button" onClick={() => setStep("details")} className="w-full h-12 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-semibold text-sm flex items-center justify-center gap-2" data-testid="gate-app-edit-details">
              <Pencil className="w-4 h-4" /> Edit details
            </button>
          </div>
          <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 px-4 py-4">
            <button
              type="button" disabled={submitting} onClick={submit} data-testid="gate-app-confirm-submit"
              className={`w-full h-16 rounded-2xl font-display font-black text-xl text-white flex items-center justify-center gap-3 disabled:opacity-60 ${form.gate_type === "IN" ? "bg-[#5a8f00] active:bg-[#4a7600]" : "bg-[#F1471D] active:bg-[#c0360e]"}`}
            >
              {submitting ? <><Loader2 className="w-6 h-6 animate-spin" /> Saving…</> : <><Check className="w-7 h-7" strokeWidth={3} /> CONFIRM GATE {form.gate_type}</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
